import {Action, Card, useDialog} from 'platform/components';
import {Box} from 'platform/foundation';

import {mergeAll} from 'ramda';

import {useGetInterestV2Query} from '@dms/api/finance';
import {
  DOCUMENT_ACTION_COLUMNS,
  useGetDocumentDatagridActions,
} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  DmsDataGrid,
  type DmsDataGridProps,
  refreshDmsDatagrid,
  Section,
  UploadDocumentsDialog,
} from '@dms/shared';

import {buildArray} from 'shared/web';

import type {QueryFilterObject} from 'features/eag-grid';

const INTEREST_DOCUMENTS_GRID_CODE = 'document-context-list-interest';
const CONTEXT_TARGET = 'interest' as const;

const refreshDataGrid = () => refreshDmsDatagrid(INTEREST_DOCUMENTS_GRID_CODE);

interface DocumentsProps extends Pick<DmsDataGridProps, 'maximization'> {
  interestId: string;
}

export function Documents(props: DocumentsProps) {
  const [isUploadDialogOpen, openUploadDialog, closeUploadDialog] = useDialog();

  const {data: interest} = useGetInterestV2Query({id: props.interestId});

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {interestId: props.interestId}]);

  const [actionCallback, bulkSignatureAction] = useGetDocumentDatagridActions({
    contextId: props.interestId,
    contextTarget: CONTEXT_TARGET,
    customerId: interest?.customerId,
    refreshData: refreshDataGrid,
  });

  const documentsActions = buildArray<Action>([
    {
      type: 'button',
      variant: 'link',
      leftIcon: 'action/backup',
      title: i18n.t('entity.document.actions.uploadDocument'),
      onClick: openUploadDialog,
      'data-testid': testIds.interest.detail('documents-upload'),
    },
  ]).add(...bulkSignatureAction);

  return (
    <Section data-testid={testIds.interest.detail('documents')}>
      <Card title={i18n.t('general.labels.documents')} actions={documentsActions}>
        <Box height={130} minHeight={50} position="relative">
          <DmsDataGrid
            maximization={props.maximization}
            data-testid={testIds.interest.detail('documents-grid')}
            gridCode={INTEREST_DOCUMENTS_GRID_CODE}
            emptyState={{headline: i18n.t('general.labels.noData')}}
            actionCallback={actionCallback}
            predefinedColumns={DOCUMENT_ACTION_COLUMNS}
            queryModifier={queryModifier}
            hasSilentRefetching
          />
        </Box>
      </Card>

      <UploadDocumentsDialog
        isOpen={isUploadDialogOpen}
        handleClose={closeUploadDialog}
        onDocumentCreated={refreshDataGrid}
        contextTarget={CONTEXT_TARGET}
        contextId={props.interestId}
        data-testid={testIds.interest.detail('documents-upload')}
      />
    </Section>
  );
}
